import ErrorMessage from "@/components/error-message";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "@/components/ui/empty";
import { useTranslation } from "@/hooks/useTranslation";
import {
  companyRefusalKey,
  type CompanyRefusalKey,
} from "@/lib/company-refusal";
import { cn } from "@/lib/utils";
import { GaugeIcon, ShieldAlertIcon } from "lucide-react";

/**
 * The run ended on the team's spent daily enrichment budget: the cards show
 * what the company already held, and this says why the rest is missing —
 * rather than each card reading as "nothing found".
 */
export function CompanyBudgetSpentAlert({ className }: { className?: string }) {
  const { t } = useTranslation();

  return (
    <Alert variant="warning" className={className}>
      <GaugeIcon />
      <AlertDescription className="text-current">
        {t("company.alerts.budgetSpent")}
      </AlertDescription>
    </Alert>
  );
}

/**
 * The company could not be opened because the API refused it — the licence,
 * the selected team, a throttle — named instead of the generic error.
 */
export function CompanyRefusalMessage({
  refusal,
  className,
}: {
  refusal: CompanyRefusalKey;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <Empty className={cn("flex-none", className)}>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <ShieldAlertIcon />
        </EmptyMedia>
        <EmptyDescription>{t(refusal)}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

/**
 * A company read that failed: the refusal named when the API refused it
 * (licence, team, daily reads, throttle), the generic error otherwise.
 */
export function CompanyErrorMessage({
  error,
  className,
}: {
  error: unknown;
  className?: string;
}) {
  const refusal = companyRefusalKey(error);

  return refusal ? (
    <CompanyRefusalMessage refusal={refusal} className={className} />
  ) : (
    <ErrorMessage className={className} />
  );
}
